import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Button } from '@zinc/ui';
import { Field, Link } from 'bz-core-controls';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import QueryString from 'query-string';
import React from 'react';
import { cache } from 'tm3-core/cache';
import { z } from 'zod';
import { WhatIsAPassKey } from '../2fa/setup/WhatIsAPassKey';
import { navigateToRedirectTarget } from '../redirect-target';
import Error from './controls/error';
import LoginStore from './Store';

const emailFilter = /[A-Za-z]|\d|\.|\-|\@|\_|\+/;

const LoginSchema = z.object({
  userName: z
    .string()
    .min(1, 'Email address must not be empty.')
    .email({ message: 'Please enter a valid email address.' }),
  password: z.string().min(1, 'Password must not be empty.')
});

interface IProps {}
@observer
class Login extends React.Component<IProps> {
  displayName = 'LoginPanel';
  loginStore: LoginStore;
  @observable userName = '';
  @observable password = '';
  @observable showPassword = false;
  @observable error = '';

  constructor(props: IProps) {
    super(props);
    this.loginStore = new LoginStore();
  }

  componentDidMount(): void {
    this.applyFailureFromQuery();
  }

  @action.bound
  handleIsAutoFill = (autofill: boolean) => {
    this.isAutoFill = autofill;
  };

  @action.bound
  onLoginWithFido(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) {
    e.preventDefault();
    this.loginStore
      .onFidoLogin()
      .then((res: boolean) => {
        if (!res) return;
        navigateToRedirectTarget();
      })
      .catch((err) => {
        this.error = err;
      });
  }

  validate(): string[] {
    const result = LoginSchema.safeParse({ userName: this.userName, password: this.password });
    if (result.success) return [];
    return result.error.issues.map((issue) => issue.message);
  }

  @action.bound
  login(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) {
    e.preventDefault();

    const validationErrors = this.validate();
    if (validationErrors.length) {
      this.error = validationErrors.join(' ');
      return;
    }

    const userName = this.userName;
    const password = this.password;
    return this.loginStore
      .login({ userName, password })
      .then((res: boolean) => {
        if (!res) return;
        navigateToRedirectTarget();
      })
      .catch((err) => {
        this.error = err;
      });
  }

  @action.bound
  usernameChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.userName = e.target.value;
  }

  @action.bound
  passwordChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.password = e.target.value;
  }

  @action.bound
  toggleShowPassword(e: React.MouseEvent<HTMLElement>) {
    e.preventDefault();
    this.showPassword = this.showPassword ? false : true;
  }

  @action.bound
  private applyFailureFromQuery() {
    const query = QueryString.parse(window.location.search);
    const failureMessageRaw = query.fm;
    const failureCodeRaw = query.f;

    const failureMessage =
      typeof failureMessageRaw === 'string'
        ? failureMessageRaw
        : Array.isArray(failureMessageRaw)
          ? failureMessageRaw[0]
          : '';

    const failureCode =
      typeof failureCodeRaw === 'string'
        ? failureCodeRaw
        : Array.isArray(failureCodeRaw)
          ? failureCodeRaw[0]
          : '';

    const hasFailureMessage =
      typeof failureMessage === 'string' && failureMessage.trim().length > 0;
    const hasFailureCode = typeof failureCode === 'string' && failureCode.trim().length > 0;

    if (!hasFailureMessage && !hasFailureCode) {
      return;
    }

    if (hasFailureMessage) {
      this.error = failureMessage.trim();
    } else if (hasFailureCode) {
      const trimmedFailureCode = failureCode.trim();
      this.error = `We could not sign you in with Microsoft (reason: ${trimmedFailureCode}). Please try again or sign in with your password.`;
    }
  }

  @computed get canDoPasswordless() {
    return typeof PublicKeyCredential != 'undefined';
  }

  render() {
    const { canDoPasswordless, showPassword } = this;
    const { loginMode } = cache.app;

    return (
      <>
        <form
          id="form-login"
          onKeyUp={(event: any) => {
            if (event.key == 'Enter') this.login(event);
          }}
        >
          <h3 className="mb-4 text-3xl font-bold">Sign in to your account</h3>

          <Field
            type="email"
            label="Email Address"
            placeholder="abc@example.com"
            onChange={this.usernameChange.bind(this)}
            validationRegEx={emailFilter}
          />
          <Field
            inputAttributes={{ type: showPassword ? 'text' : 'password' }}
            type={showPassword ? 'text' : 'password'}
            label="Password"
            endIcon={
              <div
                tabIndex={-1}
                onClick={this.toggleShowPassword}
                className="h-5 w-5 cursor-pointer bg-white stroke-gray-600 hover:stroke-gray-800"
              >
                {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
              </div>
            }
            onChange={this.passwordChange.bind(this)}
          />
          <Link to="/login/help">Forgot your password?</Link>
          <Error message={this.error} />
          <div className="mt-2 grid">
            <Button color="primary" onClick={this.login}>
              Sign In
            </Button>
          </div>
        </form>
        {loginMode === '/api/auth/ms-graph' && (
          <div className="mt-2 flex flex-col">
            <Button
              className={`!bg-indigo-500 hover:!bg-indigo-600 !text-white`}
              onClick={() =>
                (window.location = `${cache.app.loginMode}?redirect=${window.location.origin}`)
              }
            >
              Sign In with Microsoft
            </Button>
          </div>
        )}
        <div className="mt-2 flex flex-col">
          {canDoPasswordless && (
            <Button
              color="positive"
              className={`${!!process.env.isClick && '!bg-clickPink hover:!bg-clickPink'}`}
              onClick={this.onLoginWithFido}
            >
              Sign In with Passkey
            </Button>
          )}
          {this.loginStore.fidoStore.message && (
            <details className="my-2 rounded-md border border-rose-300 bg-rose-100 p-3 text-center text-rose-600">
              <summary>
                <span>There was an error signing in by Passkey</span>
              </summary>
              <p className="mt-2">{this.loginStore.fidoStore.message}</p>
            </details>
          )}
          <WhatIsAPassKey />
        </div>
      </>
    );
  }
}

/**
 * Sends the credential to the the FIDO2 server for assertion
 * @param {any} assertedCredential

async function verifyAssertionWithServer(assertedCredential) {

    // Move data into Arrays incase it is super long
    let authData = new Uint8Array(assertedCredential.response.authenticatorData);
    let clientDataJSON = new Uint8Array(assertedCredential.response.clientDataJSON);
    let rawId = new Uint8Array(assertedCredential.rawId);
    let sig = new Uint8Array(assertedCredential.response.signature);
    let userHandle = new Uint8Array(assertedCredential.response.userHandle)
    const data = {
        id: assertedCredential.id,
        rawId: coerceToBase64Url(rawId),
        type: assertedCredential.type,
        extensions: assertedCredential.getClientExtensionResults(),
        response: {
            authenticatorData: coerceToBase64Url(authData),
            clientDataJSON: coerceToBase64Url(clientDataJSON),
            userHandle: userHandle !== null ? coerceToBase64Url(userHandle): null,
            signature: coerceToBase64Url(sig)
        }
    };

    let response;
    try {
        let res = await fetch("/makeAssertion", {
            method: 'POST', // or 'PUT'
            body: JSON.stringify(data), // data can be `string` or {object}!
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });

        response = await res.json();
    } catch (e) {
        showErrorAlert("Request to server failed", e);
        throw e;
    }

    console.log("Assertion Object", response);

    // show error
    if (response.status !== "ok") {
        console.log("Error doing assertion");
        console.log(response.errorMessage);
        showErrorAlert(response.errorMessage);
        return;
    }

    // show success message
    Swal.fire({
        title: 'Logged In!',
        text: 'You\'re logged in successfully.',
        type: 'success',
        timer: 2000
    });

    // redirect?
    //window.location.href = "/dashboard/" + state.user.displayName;
}


*/
export default Login;
